import {
    createSelector,
} from 'reselect';

import appSettings from 'wanderu-config';
import {
    i18nMoment,
    selectLocale,
} from 'wanderu-i18n';
import {
    extractTemplate,
    toCamelCase,
} from 'wanderu-utils';

import {
    selectAppStateSlice,
} from '../../selectors';
import {
    mapBlogLinksToImages,
    mapContent,
    mapInfoSectionContent,
    mapInfoSectionStructuredData,
    remapImageKeyToSrc,
} from '../../utils';
import {
    CARRIER_GUIDE_FAQ_BLACKLIST,
    FEATURE_MAP,
} from './constants';
import NAMESPACE from './types';

function getCarrierGuideData(appState, carrierGuideId) {
    return selectAppStateSlice(appState)[NAMESPACE][carrierGuideId];
}

function getCarrierGuideCarriers(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.carriers;
}

function getCarrierGuideStats(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.stats;
}

function getCarrierGuideContent(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.content;
}

function getNoIndex(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.no_index;
}

function getNoIndexLocale(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.no_index_locale;
}

function getQuickviewStats(appState, carrierGuideId) {
    const carrierGuideStats = getCarrierGuideStats(appState, carrierGuideId);
    return carrierGuideStats?.metrics;
}

function getLongFormContent(appState, carrierGuideId) {
    const carrierGuideContent = getCarrierGuideContent(appState, carrierGuideId);
    return extractTemplate(carrierGuideContent);
}

function getTravelReviews(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.carrierReviews;
}

function getTravelReviewsByRoute(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.routeReviews;
}

function getReviewSummary(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.reviewSummary;
}

function getConditionalModal(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.modal_dialog_enabled;
}

function getInterlinks(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.interlinks;
}

function getBestFeatures(appState, carrierGuideId) {
    const carrierGuideData = getCarrierGuideData(appState, carrierGuideId);
    return carrierGuideData?.best;
}

const selectTopRoutes = createSelector(
    getCarrierGuideData,
    (carrierGuideData) => carrierGuideData?.topRoutes,
);

const selectBlogLinks = createSelector(
    getCarrierGuideData,
    (carrierGuideData) => carrierGuideData?.blogLinks,
);

function getCarrierActiveStatus(carriers) {
    // supporting potential additional states

    if (carriers.some((carrier) => carrier.active === 'active')) {
        return 'active';
    }

    return 'inactive';
}

function mapCarrierGuideCarriers(data) {
    if (!data || !data.length) {
        return;
    }

    /* use first carrier as primary */
    // I feel like we might want to do this in the operation, not the selector
    const {
        active: _active,
        ...primaryCarrier
    } = data[0];
    const contact = Object.entries(primaryCarrier.contact)
        .reduce((contact, entry) => {
            return {
                ...contact,
                [toCamelCase(entry[0])]: entry[1],
            };
        }, {});
    const features = primaryCarrier.features
        .filter((key) => !!key);

    return {
        ...primaryCarrier,
        activeStatus: getCarrierActiveStatus(data),
        contact,
        features,
        images: remapImageKeyToSrc(primaryCarrier.images),
    };
}

function mapQuickviewStats(data) {
    if (!data) {
        return;
    }

    const {
        cities,
        countries,
        stations,
        tripsPerDay,
    } = data;

    return [{
        name: 'tripsPerDay',
        type: 'number',
        value: tripsPerDay,
    }, {
        name: 'stations',
        type: 'number',
        value: stations,
    }, {
        name: 'cities',
        type: 'number',
        value: cities,
    }, {
        name: 'countries',
        type: 'number',
        value: countries,
    }];
}

function mapBestFeatures(features = {}) {
    if (!features) {
        return;
    }

    const { covidCount } = features;

    let covidFeatures = [];
    if (covidCount > 0) {
        covidFeatures.push({
            key: 'covidCount',
            label: 'bestCovidLabel',
            count: covidCount,
        });
    }

    let generalFeatures = [];
    const countKeys = Object.keys(features)
        .filter((feature) => feature.includes('Count') && !feature.includes('covid'));

    countKeys.forEach((featureKey) => {
        if (!!FEATURE_MAP[featureKey] && features[featureKey] > 0) {
            generalFeatures.push({
                key: featureKey,
                label: FEATURE_MAP[featureKey].label,
                count: features[featureKey],
            });
        }
    });

    return {
        covidFeatures,
        generalFeatures,
    };
}

const selectCarrierGuide = getCarrierGuideData;
const selectStats = getCarrierGuideStats;
const selectLongFormContent = getLongFormContent;
const selectReviewSummary = getReviewSummary;
const selectTravelReviews = getTravelReviews;
const selectTravelReviewsByRoute = getTravelReviewsByRoute;
const selectNoIndex = getNoIndex;
const selectNoIndexLocale = getNoIndexLocale;
const selectConditionalModal = getConditionalModal;
const selectInterlinks = getInterlinks;

const selectCarrier = createSelector(
    getCarrierGuideCarriers,
    mapCarrierGuideCarriers,
);

const selectQuickviewMetrics = createSelector(
    getQuickviewStats,
    mapQuickviewStats,
);

const selectContent = createSelector(
    getCarrierGuideContent,
    mapContent,
);

const selectInfoSectionContent = createSelector(
    getCarrierGuideContent,
    mapInfoSectionContent,
);

const selectBestFeatures = createSelector(
    getBestFeatures,
    mapBestFeatures,
);

const _selectInfoSectionStructuredData = createSelector(
    selectContent,
    (content) => {
        const infoSection = mapInfoSectionStructuredData(content);
        if (!infoSection) {
            return {};
        }

        return {
            ...infoSection,
            mainEntity: infoSection.mainEntity?.filter((data) => (
                !CARRIER_GUIDE_FAQ_BLACKLIST.includes(data.acceptedAnswer.text.key)
            )),
        };
    },
);

const _selectProductStructuredData = createSelector(
    selectCarrier,
    selectReviewSummary,
    (carrier, reviewSummaryData) => {
        if (!carrier || !reviewSummaryData) {
            return;
        }

        const {
            id,
            contact,
            name,
        } = carrier;
        const {
            phone,
        } = contact;

        const telephone = phone[Object.keys(phone)[0]]?.replace(/[- )(]/g, '');

        return {
            '@context': 'http://schema.org',
            '@type': 'Organization',
            name: name,
            telephone: telephone,
            image: `${appSettings.config.s3.staticAssetsUrl}carriers/icons/${id}.png`,
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: reviewSummaryData.overall.toFixed(1),
                bestRating: 5,
                reviewCount: reviewSummaryData.review_count,
            },
        };
    },
);

const selectStructuredData = createSelector(
    _selectInfoSectionStructuredData,
    _selectProductStructuredData,
    (infoSection, product) => [
        infoSection,
        product,
    ].filter(Boolean),
);

const selectSimplifiedCarrierReviews = createSelector(
    getTravelReviewsByRoute,
    (reviews) => (
        reviews?.reduce((simpleReviews, travelReviews) => ([
            ...simpleReviews,
            ...travelReviews.individualReviews.map((review) => {
                return {
                    ...review,
                };
            }),
        ]), [])
            ?.sort((a, b) => i18nMoment(b.stats.reviewIndividualDepartDate)
                .diff(a.stats.reviewIndividualDepartDate))
    ),
);

const selectBlurbSectionData = createSelector(
    selectBlogLinks,
    (rawBlogLinks) => {
        const blogLinks = rawBlogLinks || [];

        const blogPosts = mapBlogLinksToImages(blogLinks);

        return {
            blogPosts,
        };
    },
);

const selectBlurbSectionContent = createSelector(
    selectBlurbSectionData,
    (blurbSectionData = {}) => {
        const {
            blogPosts,
        } = blurbSectionData;

        return {
            blogPosts,
        };
    },
);

const selectRenderFlags = createSelector(
    selectLocale,
    selectContent,
    selectBlurbSectionData,
    (
        locale,
        content = {},
        blurbSectionData = {},
    ) => {
        const { blogPosts } = blurbSectionData;

        const showBlogBlurbSection = locale.includes('en') && !!blogPosts.length;

        return {
            showBlogBlurbSection,
        };
    },
);

export {
    selectBestFeatures,
    selectBlurbSectionContent,
    selectCarrier,
    selectCarrierGuide,
    selectNoIndex,
    selectNoIndexLocale,
    selectConditionalModal,
    selectContent,
    selectInfoSectionContent,
    selectInterlinks,
    selectLongFormContent,
    selectQuickviewMetrics,
    selectRenderFlags,
    selectReviewSummary,
    selectSimplifiedCarrierReviews,
    selectStats,
    selectStructuredData,
    selectTopRoutes,
    selectTravelReviews,
    selectTravelReviewsByRoute,
};
